import type { LucideIcon } from 'lucide-react'
import { useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

import { cn } from '@/utils/cn'

export interface MenuAction {
  label: string
  icon?: LucideIcon
  onSelect: () => void
  tone?: 'default' | 'danger'
  disabled?: boolean
}

interface MenuProps {
  /** Gatilho do menu (um botão/ícone). Recebe o handler de toggle. */
  trigger: (props: { open: boolean; toggle: () => void; id: string }) => ReactNode
  actions: MenuAction[]
  align?: 'start' | 'end'
  className?: string
}

interface Position {
  top: number
  left: number
  /** Abre abaixo do gatilho por padrão; vira para cima quando não cabe no viewport. */
  placement: 'bottom' | 'top'
  maxHeight?: number
}

/** Distância entre o gatilho e o dropdown, e margem mínima até a borda da tela. */
const GAP = 4
const VIEWPORT_MARGIN = 8

/**
 * Popover de ações reutilizável (⋮ da tabela, "Mais ações" do detalhe).
 * O dropdown é portalizado em `document.body` (posicionado via `getBoundingClientRect`
 * do gatilho) para não ficar preso pelo `overflow` de containers com rolagem
 * própria, como `TableContainer` — senão o menu abre "dentro" da linha da tabela.
 */
export function Menu({ trigger, actions, align = 'end', className }: MenuProps) {
  const [open, setOpen] = useState(false)
  const [position, setPosition] = useState<Position | null>(null)
  const anchorRef = useRef<HTMLDivElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const id = useId()

  useLayoutEffect(() => {
    if (!open) return
    const rect = anchorRef.current?.getBoundingClientRect()
    if (!rect) return
    setPosition({
      top: rect.bottom + GAP,
      left: align === 'end' ? rect.right : rect.left,
      placement: 'bottom',
    })
  }, [open, align])

  // Com o dropdown já no DOM (ainda antes da pintura), mede a altura: se não couber abaixo
  // e houver mais espaço acima, abre para cima; se não couber em nenhum lado, limita a altura.
  useLayoutEffect(() => {
    if (!open || !position || position.placement !== 'bottom' || position.maxHeight) return
    const rect = anchorRef.current?.getBoundingClientRect()
    const menuHeight = dropdownRef.current?.offsetHeight ?? 0
    if (!rect || !menuHeight) return

    const spaceBelow = window.innerHeight - rect.bottom - GAP - VIEWPORT_MARGIN
    const spaceAbove = rect.top - GAP - VIEWPORT_MARGIN
    if (menuHeight <= spaceBelow) return

    if (spaceAbove > spaceBelow) {
      const height = Math.min(menuHeight, spaceAbove)
      setPosition({
        ...position,
        top: rect.top - GAP - height,
        placement: 'top',
        maxHeight: height < menuHeight ? height : undefined,
      })
    } else {
      setPosition({ ...position, maxHeight: spaceBelow })
    }
  }, [open, position])

  useEffect(() => {
    if (!open) return

    const close = () => setOpen(false)
    const onPointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node
      if (anchorRef.current?.contains(target) || dropdownRef.current?.contains(target)) return
      close()
    }

    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('touchstart', onPointerDown)
    // `capture: true` também pega rolagem dentro de containers internos (ex.: TableContainer).
    window.addEventListener('scroll', close, true)
    window.addEventListener('resize', close)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('touchstart', onPointerDown)
      window.removeEventListener('scroll', close, true)
      window.removeEventListener('resize', close)
    }
  }, [open])

  return (
    <div
      ref={anchorRef}
      className={cn('relative', className)}
      // Cliques no gatilho não devem acionar handlers de ancestrais
      // (ex.: seleção de linha da tabela).
      onClick={(event) => event.stopPropagation()}
    >
      {trigger({ open, toggle: () => setOpen((v) => !v), id })}

      {open &&
        position &&
        createPortal(
          <div
            ref={dropdownRef}
            role="menu"
            aria-labelledby={id}
            style={{ top: position.top, left: position.left, maxHeight: position.maxHeight }}
            onClick={(event) => event.stopPropagation()}
            className={cn(
              'pc-animate-modal fixed z-40 min-w-48 overflow-y-auto rounded-xl border border-line bg-surface py-1 shadow-lg',
              align === 'end' && '-translate-x-full',
              position.placement === 'top'
                ? align === 'end'
                  ? 'origin-bottom-right'
                  : 'origin-bottom-left'
                : align === 'end'
                  ? 'origin-top-right'
                  : 'origin-top-left',
            )}
          >
            {actions.map((action) => (
              <button
                key={action.label}
                type="button"
                role="menuitem"
                disabled={action.disabled}
                onClick={() => {
                  setOpen(false)
                  action.onSelect()
                }}
                className={cn(
                  'flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm transition-colors disabled:opacity-40',
                  action.tone === 'danger'
                    ? 'text-critical hover:bg-critical/8'
                    : 'text-fg hover:bg-surface-2',
                )}
              >
                {action.icon && <action.icon className="size-4 shrink-0" />}
                {action.label}
              </button>
            ))}
          </div>,
          document.body,
        )}
    </div>
  )
}
